<script lang="ts" setup>
import type { Section } from '~/types'

interface Props extends Pick<Section, 'id' | 'title' | 'theme' | 'subtitle' | 'expand' | 'buttons' | 'description'> { }
const props = defineProps<Props>()

const searchQuery = ref('')

const typingEffectText = ref<string[]>([])

watch(() => props.subtitle, (val) => {
  typingEffectText.value = val.split(',').map(item => item.trim())
}, { immediate: true })
function onFocus() {
  const element = document.getElementById('homepage-search')
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
</script>

<template>
  <Hero
    image="/bg.png"
    video="/wide_720p.mp4"
    mobile-video="/mobile_720x1280.mp4"
    class="-mt-24 min-h-[80vh] h-auto pb-28 pt-64 flex flex-col z-30 "
    :theme="theme"
  >
    <div class="flex w-full flex-col relative z-20">
      <HeadingText
        v-motion-pop-visible
        tag="h1"
        class=" text-yellow max-w-md flex flex-col text-8xl xl:text-9xl !leading-[0.85em] font-display"
      >
        {{ title }}
        <TypingEffect
          :text="typingEffectText"
          class="block w-full "
          text-class="uppercase text-8xl xl:text-9xl !leading-[0.85em] font-display font-bold"
        />
      </HeadingText>

      <h2
        v-motion-pop-visible
        :delay="200"
        class="w-full max-w-lg font-sans text-white text-xl md:text-2xl lg:text-3xl font-semibold"
        :theme="theme"
      >
        <span v-html="description" />
      </h2>
    </div>
    <SearchMain
      id="homepage-search"
      v-model:query="searchQuery"
      v-motion
      :visible="{ opacity: 1, y: 0, x: 0, scale: 1 }"
      :initial="{ opacity: 0, y: 10, x: 0, scale: 0.5 }"
      :delay="300"
      class="z-40 relative w-full max-w-2xl mt-12"
      search-class="max-w-4xl mx-auto flex"
      size="2xl"
      @focus="onFocus"
    >
      <template #results="{ results, visible, totalItems, query }">
        <Transition
          name="fade"
          mode="out-in"
        >
          <div
            v-show="visible"
            id="homepage-search"
            class="absolute top-full rounded-2xl mt-6 left-0 right-0 bg-white overflow-clip z-50"
          >
            <p class="font-display uppercase font-bold text-right px-6 py-1 bg-slate-200">
              {{ totalItems }} {{ totalItems === 1 ? 'Result' : 'Results' }}
            </p>
            <transition
              name="fade"
              mode="out-in"
            >
              <div
                v-if="results?.length"
                class="p-6 overflow-y-auto max-h-[60vh] results"
              >
                <ul
                  v-for="(category, index) in results"
                  :key="index"
                  class="pb-3"
                >
                  <li>
                    <div
                      v-if="category.type === 'Pages'"
                      class="font-display text-lg lg:text-xl font-bold px-2 rounded-full uppercase flex justify-between"
                    >
                      {{ category.type }} <p
                        class="font-display uppercase font-bold text-right px-3 py-1 bg-slate-200 text-sm inline-flex items-center justify-center"
                      >
                        {{ category.totalItems }} {{ category.totalItems === 1 ? 'Result' : 'Results' }}
                      </p>
                    </div>
                    <NuxtLink
                      v-else
                      :to="`${getCollectionRoute(category.type)}?q=${query}`"
                      class="font-display text-lg duration-100 lg:text-xl font-bold hover:text-pink px-2 rounded-full uppercase flex justify-between"
                    >
                      {{ category.type }} <p
                        class="font-display uppercase font-bold text-right px-3 py-1 bg-slate-200 text-sm inline-flex items-center justify-center"
                      >
                        {{ category.totalItems }} {{ category.totalItems === 1 ? 'Result' : 'Results' }}
                      </p>
                    </NuxtLink>
                  </li>
                  <li
                    v-for="item in category.results"
                    :key="item.id"
                    class="pt-1"
                  >
                    <NuxtLink
                      :to="getPublicRoute(category.type, item)"
                      class="text-primary-700 py-2 rounded-full hover:bg-slate-100 px-2 font-bold"
                      active-class="text-primary-800 text-white"
                    >
                      {{ item.slug === 'homepage' ? 'Homepage' : item.name ? item.name : item.title }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
              <div
                v-else-if="query?.length && query.length < 2"
                class="p-6"
              >
                <p class="text-center">
                  Continue typing to get results
                </p>
              </div>
              <div
                v-else
                class="p-6"
              >
                <p class="text-center">
                  No results found
                </p>
              </div>
            </transition>
          </div>
        </Transition>
      </template>
      <template #default="{ goToSearch }">
        <UButton
          class="bg-primary-800 px-5 font-black font-display"
          trailing
          size="2xl"
          @click="goToSearch"
        >
          GO
          <Icon
            name="gbf:arrow-right"
            class="text-yellow h-8 w-8 animate-side-ping"
          />
        </UButton>
      </template>
    </SearchMain>
    <template #background>
      <div class="w-[50%] h-full bg-blue left-0 opacity-80 z-10 absolute" />
      <div class="w-[50%] h-full bg-pink right-0 opacity-80 z-10 absolute" />
    </template>
  </Hero>
</template>

<style scoped>
#homepage-search {
  scroll-margin-top: 100px;
}

.results::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  background-color: #F5F5F5;
}

.results::-webkit-scrollbar {
  width: 6px;
  background-color: #F5F5F5;
}

.results::-webkit-scrollbar-thumb {
  @apply bg-blue;
}
</style>
